import { NotificationItem, useDismissAlertMutation, useNotificationsQuery } from "@/actions/notification.actions";
import { useAuthStore } from "@/states";
import { App, Button } from "antd";
import { XIcon } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { ProductPageProvider } from "../ProductPage";
import { ViewNotificationModal } from "./ViewNotificationModal";
import { getNotificationIcon, getTypeTag } from "./notificationIcons";

/** Shows a bounded, dismissible alert stack without covering the entire viewport. */
export const NotificationAlertsBanner = () => {
  const user = useAuthStore((state) => state.user);
  const { message } = App.useApp();
  const { data } = useNotificationsQuery({ poll: true });
  const dismiss = useDismissAlertMutation();
  const [selected, setSelected] = useState<NotificationItem | null>(null);
  const alerts = data?.activeAlerts || [];
  const activeAlertCount = data?.activeAlertCount || 0;
  if (!user || user.role === "super_admin") return null;
  return (
    <ProductPageProvider>
      {alerts.length > 0 && <aside aria-label="Important notifications" aria-live="polite"
        className="pointer-events-none fixed right-3 top-20 z-40 flex max-h-[calc(100dvh-6rem)] w-[calc(100vw-1.5rem)] max-w-sm flex-col gap-2 overflow-y-auto overscroll-contain sm:right-6">
        {alerts.map((alert, index) => (
          <div key={alert.id} className={`pointer-events-auto flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-3 text-[#131B2A] shadow-sm ${index > 0 ? "hidden sm:flex" : ""}`}>
            {getNotificationIcon(alert.type, alert, "sm")}
            <div className="min-w-0 flex-1">
              {getTypeTag(alert.type)}
              <p className="mt-1 line-clamp-2 break-words text-sm font-semibold [overflow-wrap:anywhere]">{alert.title}</p>
              <p className="mt-1 line-clamp-2 break-words text-xs leading-5 text-gray-600 [overflow-wrap:anywhere]">{alert.message}</p>
              <Button type="link" size="small" onClick={() => setSelected(alert)} className="!h-auto !px-0 !text-xs !text-[#131B2A]">View details</Button>
            </div>
            <Button type="text" aria-label={`Dismiss alert: ${alert.title}`} icon={<XIcon size={16} />} className="shrink-0"
              loading={dismiss.isPending && dismiss.variables === alert.id}
              onClick={() => dismiss.mutate(alert.id, { onError: () => message.error("Failed to dismiss alert") })} />
          </div>
        ))}
        <Link to="/notifications" className="pointer-events-auto rounded-lg border border-gray-200 !bg-white px-3 py-2 text-center text-xs font-medium !text-[#131B2A] focus-visible:outline-2 focus-visible:outline-[#DDA74F]">
          View all notifications ({activeAlertCount} active)
        </Link>
      </aside>}
      <ViewNotificationModal open={Boolean(selected)} notification={selected} onClose={() => setSelected(null)} />
    </ProductPageProvider>
  );
};
